import {
  insertAtIndex,
  move,
  removeIndex,
} from "@prismicio/editor-support/Array"
import { uniqueNumber } from "@prismicio/editor-support/Number"
import {
  AnimatedElement,
  Box,
  Group,
  GroupItem,
  Icon,
  LazyRender,
  Line,
  type RenderDropIndicatorArgs,
  Text,
} from "@prismicio/editor-ui"
import {
  type GroupContent,
  type GroupItemContent,
  type NestableContent,
} from "@prismicio/types-internal/lib/content"
import type {
  Group as GroupWidget,
  NestableWidget,
} from "@prismicio/types-internal/lib/customtypes"
import { type Key, useRef } from "react"

import { useEditorConfig } from "../EditorConfig"
import { GroupAndNestableWidgetSwitch } from "../GroupAndNestableWidgetSwitch"

type RepeatableZoneEditorProps = {
  id: string
  readOnly: boolean
  /**
   * If set, the Zone is limited to no more than 0 or 1 item.
   * Defaults to true.
   */
  repeatable?: boolean
  repeatableZone: Readonly<Record<string, NestableWidget | GroupWidget>>
  content: GroupItemContent[] | undefined
  onContentChange: (content: GroupItemContent[]) => void
} & (
  | { title: string; addItemButtonTitle: string }
  | { title?: undefined; addItemButtonTitle?: undefined }
)

export function RepeatableZoneEditor(props: RepeatableZoneEditorProps) {
  const {
    id,
    title,
    addItemButtonTitle,
    content,
    onContentChange,
    readOnly,
    repeatable = true,
    repeatableZone,
  } = props

  const repeatableWidgets = Object.entries(repeatableZone)

  let repeatableZoneContent = content ?? [emptyGroupItemContent]

  // For some time, we didn't support repeat = false which means people were able to save Groups
  // with more than one item when they shouldn't have been able to.
  // Get rid of the extra items here for the next time they save.
  if (!repeatable && repeatableZoneContent.length > 1) {
    repeatableZoneContent = repeatableZoneContent.slice(0, 1)
  }

  const { scrollOffsetTop } = useEditorConfig()

  const itemKeyMap = useRef(new WeakMap<GroupItemContent, string>())

  useSynchronizeItemKeyMap(itemKeyMap.current, repeatableZoneContent)

  function getItemKey(item: GroupItemContent) {
    return itemKeyMap.current.get(item) ?? "!bugged!"
  }

  function onAddItem(atIndex: number) {
    const item = createGroupItemContent()
    onContentChange(insertAtIndex(repeatableZoneContent, atIndex, item))
  }

  function onMoveItem(args: { index: number; offset: number }) {
    const { index, offset } = args
    onContentChange(move(repeatableZoneContent, index, index + offset))
  }

  function onDeleteItem(index: number) {
    onContentChange(removeIndex(repeatableZoneContent, index))
  }

  function onWidgetChange(
    groupItemContent: GroupItemContent,
    index: number,
    repeatableWidgetId: string,
    widgetContent: NestableContent | GroupContent | undefined,
  ) {
    const updatedItem = updateRepeatableZoneItem(groupItemContent, [
      repeatableWidgetId,
      widgetContent,
    ])
    const updatedZone = updateRepeatableZone(repeatableZoneContent, [
      index,
      updatedItem,
    ])
    updateItemKeyMap(itemKeyMap.current, groupItemContent, updatedItem)
    onContentChange(updatedZone)
  }

  if (Object.keys(repeatableZone).length === 0) return null

  return (
    <Group
      title={title ?? "Repeatable zone"}
      addItemButtonTitle={addItemButtonTitle ?? "Add item in repeatable zone"}
      items={repeatableZoneContent}
      maxItems={repeatable ? undefined : 1}
      getItemKey={getItemKey}
      onAddItem={onAddItem}
      dnd={{
        scrollOffsetTop,
        onReorder: onContentChange,
        renderDragPreview: ({ index }) => (
          <DragPreview title={title ?? "Repeatable zone"} index={index} />
        ),
        renderDropIndicator,
      }}
    >
      {(groupItem, groupIndex) => {
        const valueMap = new Map(groupItem.value)
        const key = getItemKey(groupItem)

        return (
          <LazyRender keepLoaded loadWhenSearching>
            <GroupItem
              index={groupIndex}
              title={`${groupIndex + 1} • Item`}
              onMove={onMoveItem}
              onDelete={onDeleteItem}
            >
              {repeatableWidgets.map(([widgetId, widget]) => {
                return (
                  <GroupAndNestableWidgetSwitch
                    id={`${id}[${key}].${widgetId}`}
                    key={widgetId}
                    content={valueMap.get(widgetId)}
                    widget={widget}
                    onContentChange={(content) =>
                      onWidgetChange(groupItem, groupIndex, widgetId, content)
                    }
                    readOnly={readOnly}
                  />
                )
              })}
            </GroupItem>
          </LazyRender>
        )
      }}
    </Group>
  )
}

// The Prismic model for a GroupItemContent does not have a stable ID for us to use.
// We generate an id on the client to get a stable React key and ensure moving, updating items, etc works correctly.
// The more traditional way to do this is to add the ID at server body validation time
// but here we're dealing with a very complex Document data structure that we provide back to the server as-is so we're trying not to make structural changes to it.
function useSynchronizeItemKeyMap(
  map: WeakMap<GroupItemContent, Key>,
  items: GroupItemContent[],
): void {
  // For each new GroupItemContent, generate an id.
  items.forEach((item) => {
    if (!map.has(item)) map.set(item, uniqueNumber())
  })
}

function updateItemKeyMap(
  map: WeakMap<GroupItemContent, Key>,
  oldItem: GroupItemContent,
  newItem: GroupItemContent,
) {
  const currentId = map.get(oldItem)

  // Unknown item: should not happen.
  if (currentId === undefined) return

  map.set(newItem, currentId)
}

// Note: We need a stable reference for this. See "synchronizeItemKeyMap".
// Immutable updates will ensure we never write to this object.
const emptyGroupItemContent: GroupItemContent = {
  __TYPE__: "GroupItemContent",
  value: [],
}

function updateRepeatableZone(
  repeatableZoneContent: GroupItemContent[],
  updatedItemWithIndex: [number, GroupItemContent | undefined],
): GroupItemContent[] {
  const [updatedItemIndex, updatedItemValue] = updatedItemWithIndex

  if (!updatedItemValue) {
    return repeatableZoneContent.filter(
      (_i, index) => index !== updatedItemIndex,
    )
  }

  return repeatableZoneContent.map<GroupItemContent>((currentItem, index) => {
    if (index === updatedItemIndex) return updatedItemValue
    return currentItem
  })
}

function updateRepeatableZoneItem(
  repeatableItemContent: GroupItemContent,
  updatedWidgetWithId: [string, NestableContent | GroupContent | undefined],
): GroupItemContent {
  const [updatedWidgetId, updatedWidgetValue] = updatedWidgetWithId

  if (!updatedWidgetValue) {
    const value = repeatableItemContent.value.filter(([id]) => {
      return id !== updatedWidgetId
    })

    return createGroupItemContent(value)
  }

  const value = Array.from(
    new Map(repeatableItemContent.value).set(
      updatedWidgetId,
      updatedWidgetValue,
    ),
  )

  return createGroupItemContent(value)
}

function createGroupItemContent(
  value: [string, NestableContent | GroupContent][] = [],
): GroupItemContent {
  return {
    __TYPE__: "GroupItemContent",
    value,
  }
}

interface DragPreviewProps {
  title: string
  index: number
}

function DragPreview(props: DragPreviewProps) {
  const { title, index } = props

  return (
    <Box
      padding={{ block: 8, inline: 12 }}
      backgroundColor="grey3"
      border
      borderRadius={6}
      alignItems="center"
      justifyContent="space-between"
      width="100%"
    >
      <div>
        <Text variant="smallBold" color="grey11">
          {title ?? "Repeatable zone"}
        </Text>
        <Text variant="small" color="grey11">
          {index + 1} • Item
        </Text>
      </div>
      <Icon name="dragIndicator" size="small" color="grey8" />
    </Box>
  )
}

function renderDropIndicator(args: RenderDropIndicatorArgs) {
  const { isDropTarget, itemIndex, position } = args

  const line = isDropTarget ? (
    <Line
      key="line"
      color="purple"
      thickness={2}
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: itemIndex === 0 && position === "before" ? 0 : undefined,
      }}
    />
  ) : undefined

  return <AnimatedElement enterDuration={150}>{line}</AnimatedElement>
}
